import type { DecodedImage } from '../types'
import { inspectRasterDimensions } from '../core/imageHeader'

const HEIC_EXTENSIONS = /\.(?:heic|heif)$/i

function once(action: () => void): () => void {
  let completed = false
  return () => {
    if (completed) return
    completed = true
    action()
  }
}
export const MAX_IMAGE_PIXELS = 50_000_000
export const MAX_IMAGE_DIMENSION = 20_000

function assertSafeDimensions(width: number, height: number): void {
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width <= 0 || height <= 0) {
    throw new Error('画像の縦横サイズを確認できませんでした')
  }
  if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION || width * height > MAX_IMAGE_PIXELS) {
    throw new Error('この画像は高解像度すぎるため処理できません。最大5,000万画素・一辺20,000pxまでです')
  }
}

function isHeicLike(file: File): boolean {
  return HEIC_EXTENSIONS.test(file.name) || /image\/(?:heic|heif|heic-sequence|heif-sequence)/i.test(file.type)
}

async function decodeNative(file: Blob, sourceName: string, sourceMime: string): Promise<DecodedImage> {
  if ('createImageBitmap' in window) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
      return {
        drawable: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        sourceName,
        sourceMime,
        release: once(() => bitmap.close()),
      }
    } catch {
      // Safari and some HEIC/JPEG variants fall through to HTMLImageElement.
    }
  }

  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.decoding = 'async'
    image.src = url
    await image.decode()
    return {
      drawable: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      sourceName,
      sourceMime,
      release: once(() => URL.revokeObjectURL(url)),
    }
  } catch (error) {
    URL.revokeObjectURL(url)
    throw error
  }
}

export async function decodeImageFile(file: File): Promise<DecodedImage> {
  if (file.size === 0) throw new Error('空の画像ファイルです')
  const maxBytes = 80 * 1024 * 1024
  if (file.size > maxBytes) throw new Error('80MBを超える画像は端末のメモリ不足を防ぐため処理できません')

  const headerDimensions = await inspectRasterDimensions(file)
  if (headerDimensions) assertSafeDimensions(headerDimensions.width, headerDimensions.height)

  let nativeDecoded: DecodedImage | undefined
  try {
    nativeDecoded = await decodeNative(file, file.name, file.type || 'application/octet-stream')
  } catch (nativeError) {
    if (!isHeicLike(file)) throw new Error(`画像を読み込めませんでした: ${nativeError instanceof Error ? nativeError.message : '未対応形式'}`)
  }
  if (nativeDecoded) {
    try { assertSafeDimensions(nativeDecoded.width, nativeDecoded.height) } catch (error) { nativeDecoded.release(); throw error }
    return nativeDecoded
  }

  try {
    const { heicTo } = await import('heic-to/csp')
    const converted = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.96 })
    const blob = Array.isArray(converted) ? converted[0] : converted
    if (!(blob instanceof Blob)) throw new Error('HEIC変換結果が不正です')
    const decoded = await decodeNative(blob, file.name, file.type || 'image/heic')
    try { assertSafeDimensions(decoded.width, decoded.height) } catch (error) { decoded.release(); throw error }
    return decoded
  } catch (error) {
    throw new Error(`HEIC/HEIF画像を変換できませんでした: ${error instanceof Error ? error.message : '変換エラー'}`)
  }
}

export function drawDecodedToCanvas(decoded: DecodedImage, maxLongEdge = Number.POSITIVE_INFINITY): { canvas: HTMLCanvasElement; scale: number } {
  const longEdge = Math.max(decoded.width, decoded.height)
  const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1
  const width = Math.max(1, Math.round(decoded.width * scale))
  const height = Math.max(1, Math.round(decoded.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('Canvas 2Dを利用できません')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(decoded.drawable, 0, 0, width, height)
  return { canvas, scale }
}


export function createPreviewDecoded(decoded: DecodedImage, maxLongEdge = 2200): DecodedImage {
  const longEdge = Math.max(decoded.width, decoded.height)
  const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1
  const previewWidth = Math.max(1, Math.round(decoded.width * scale))
  const previewHeight = Math.max(1, Math.round(decoded.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = previewWidth
  canvas.height = previewHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('編集用プレビューを作成できません')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(decoded.drawable, 0, 0, previewWidth, previewHeight)
  return {
    drawable: canvas,
    width: decoded.width,
    height: decoded.height,
    sourceName: decoded.sourceName,
    sourceMime: decoded.sourceMime,
    release: once(() => {
      canvas.width = 1
      canvas.height = 1
    }),
  }
}
